import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { 
  Search, 
  Building2, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  ChevronDown,
  ChevronRight,
  FileText
} from 'lucide-react';

function ContactCard({ contact, parentName, parentType }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-gray-900">{contact.name}</h4>
          {contact.title && (
            <p className="text-sm text-gray-500">{contact.title}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {parentType}: {parentName}
          </p>
        </div>
        {contact.isPrimary && (
          <span className="px-2 py-0.5 bg-hub-100 text-hub-700 text-xs rounded-full">
            Primary
          </span>
        )}
      </div>
      <div className="mt-3 space-y-1">
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-hub-600">
            <Phone className="w-4 h-4" />
            {contact.phone}
          </a>
        )}
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-hub-600">
            <Mail className="w-4 h-4" />
            {contact.email}
          </a>
        )}
      </div>
      {contact.notes && (
        <p className="mt-2 text-xs text-gray-500 border-t pt-2">{contact.notes}</p>
      )}
    </div>
  );
}

function ClientCard({ client, expanded, onToggle }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button 
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-hub-100 text-hub-600 flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{client.name}</h3>
            <p className="text-sm text-gray-500">
              {client.venues?.length || 0} venues • {client.contacts?.length || 0} contacts
            </p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
          {client.billingAddress && (
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-gray-600">{client.billingAddress}</span>
            </div>
          )}
          {client.paymentTerms && (
            <div className="flex items-start gap-2 text-sm">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-gray-600">Payment Terms: {client.paymentTerms}</span>
            </div>
          )}
          {client.notes && (
            <p className="text-sm text-gray-600 bg-white p-3 rounded border">{client.notes}</p>
          )}
          
          {client.contacts?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Contacts</h4>
              <div className="grid gap-2">
                {client.contacts.map(contact => (
                  <ContactCard 
                    key={contact.id} 
                    contact={contact} 
                    parentName={client.name}
                    parentType="Client"
                  />
                ))}
              </div>
            </div>
          )}
          
          {client.venues?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Venues</h4>
              <div className="flex flex-wrap gap-2">
                {client.venues.map(venue => (
                  <span key={venue.id} className="px-3 py-1 bg-white border rounded-full text-sm">
                    {venue.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VenueCard({ venue, expanded, onToggle }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button 
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
            <MapPin className="w-5 h-5" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{venue.name}</h3>
            <p className="text-sm text-gray-500">
              {venue.client?.name} • {venue.locations?.length || 0} locations
            </p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
          {venue.address && (
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-gray-600">{venue.address}</span>
            </div>
          )}
          {venue.notes && (
            <p className="text-sm text-gray-600 bg-white p-3 rounded border">{venue.notes}</p>
          )}
          
          {venue.contacts?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Contacts</h4>
              <div className="grid gap-2">
                {venue.contacts.map(contact => (
                  <ContactCard 
                    key={contact.id} 
                    contact={contact} 
                    parentName={venue.name}
                    parentType="Venue"
                  />
                ))}
              </div>
            </div>
          )}
          
          {venue.locations?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Locations</h4>
              <div className="flex flex-wrap gap-2">
                {venue.locations.map(loc => (
                  <span key={loc.id} className="px-3 py-1 bg-white border rounded-full text-sm">
                    {loc.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkDirectory() {
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedVenues, setExpandedVenues] = useState({});

  useEffect(() => {
    loadDirectory();
  }, [search, activeTab]);

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (activeTab !== 'all') params.append('type', activeTab);
      
      const result = await api.get(`/directory/work?${params.toString()}`);
      setData(result);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleClient = (id) => {
    setExpandedClients(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleVenue = (id) => {
    setExpandedVenues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'clients', label: 'Clients' },
    { id: 'venues', label: 'Venues' },
    { id: 'contacts', label: 'Contacts' }
  ];

  const allContacts = [...(data?.clientContacts || []), ...(data?.venueContacts || [])];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Work Directory</h1>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search clients, venues, contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-hub-500 text-hub-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Clients */}
          {(activeTab === 'all' || activeTab === 'clients') && data?.clients?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Clients ({data.clients.length})
              </h2>
              <div className="space-y-2">
                {data.clients.map(client => (
                  <ClientCard
                    key={client.id}
                    client={client}
                    expanded={expandedClients[client.id]}
                    onToggle={() => toggleClient(client.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Venues */}
          {(activeTab === 'all' || activeTab === 'venues') && data?.venues?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Venues ({data.venues.length})
              </h2>
              <div className="space-y-2">
                {data.venues.map(venue => (
                  <VenueCard
                    key={venue.id}
                    venue={venue}
                    expanded={expandedVenues[venue.id]}
                    onToggle={() => toggleVenue(venue.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Contacts */}
          {(activeTab === 'all' || activeTab === 'contacts') && allContacts.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-5 h-5" />
                Contacts ({allContacts.length})
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.clientContacts?.map(contact => (
                  <ContactCard
                    key={`client-${contact.id}`}
                    contact={contact}
                    parentName={contact.client?.name}
                    parentType="Client"
                  />
                ))}
                {data.venueContacts?.map(contact => (
                  <ContactCard
                    key={`venue-${contact.id}`}
                    contact={contact}
                    parentName={contact.venue?.name}
                    parentType="Venue"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!data?.clients?.length && !data?.venues?.length && !allContacts.length && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No results found</p>
              {search && <p className="text-sm">Try a different search term</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
